import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "../../../../services/axiosHttp";

export const fetchRegionList = createAsyncThunk("fetchRegionList", async ({ page, limit, filters }, { rejectWithValue }) => {
	try {
		const params = new URLSearchParams({
			page: page,
			limit: limit,
			keyword: filters?.keyword ?? "",
			company_id: filters?.company_id ?? "",
		});

		const response = await axiosHttp.get(`region/list?${params}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const fetchRegionDetail = createAsyncThunk("fetchRegionDetail", async (id, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`region/detail/${id}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const createRegion = createAsyncThunk("createRegion", async (data, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("region/create", data);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const updateRegion = createAsyncThunk("updateRegion", async ({ id, data }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`region/update?id=${id}`, data);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const assignRegionalManager = createAsyncThunk("assignRegionalManager", async (data, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("sales-assignment/create", data);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const unassignRegionalManager = createAsyncThunk("unassignRegionalManager", async (assignmentId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`sales-assignment/delete/${assignmentId}`);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

const regionSlice = createSlice({
	name: "region",
	initialState: {
		list: {
			isLoading: false,
			data: [],
			error: null,
			page: 1,
			total: 0,
		},
		detail: {
			isLoading: false,
			data: null,
			error: null,
		},
		create: {
			isLoading: false,
			success: false,
			error: null,
		},
		update: {
			isLoading: false,
			success: false,
			error: null,
		},
		assign: {
			isLoading: false,
			success: false,
			error: null,
		},
		unassign: {
			isLoading: false,
			success: false,
			error: null,
		},
	},
	reducers: {
		resetCreateState: (state) => {
			state.create.isLoading = false;
			state.create.success = false;
			state.create.error = null;
		},
		resetUpdateState: (state) => {
			state.update.isLoading = false;
			state.update.success = false;
			state.update.error = null;
		},
		resetDetailState: (state) => {
			state.detail.isLoading = false;
			state.detail.data = null;
			state.detail.error = null;
		},
		resetAssignState: (state) => {
			state.assign.isLoading = false;
			state.assign.success = false;
			state.assign.error = null;
		},
		resetUnassignState: (state) => {
			state.unassign.isLoading = false;
			state.unassign.success = false;
			state.unassign.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchRegionList.pending, (state) => {
				state.list.isLoading = true;
				state.list.error = null;
			})
			.addCase(fetchRegionList.fulfilled, (state, action) => {
				state.list.isLoading = false;
				state.list.data = action.payload.docs || [];
				state.list.page = action.payload.current_page;
				state.list.total = action.payload.total;
			})
			.addCase(fetchRegionList.rejected, (state, action) => {
				state.list.isLoading = false;
				state.list.error = action.payload;
			})

			.addCase(fetchRegionDetail.pending, (state) => {
				state.detail.isLoading = true;
				state.detail.error = null;
			})
			.addCase(fetchRegionDetail.fulfilled, (state, action) => {
				state.detail.isLoading = false;
				state.detail.data = action.payload;
			})
			.addCase(fetchRegionDetail.rejected, (state, action) => {
				state.detail.isLoading = false;
				state.detail.error = action.payload;
			})

			.addCase(createRegion.pending, (state) => {
				state.create.isLoading = true;
				state.create.success = false;
				state.create.error = null;
			})
			.addCase(createRegion.fulfilled, (state) => {
				state.create.isLoading = false;
				state.create.success = true;
			})
			.addCase(createRegion.rejected, (state, action) => {
				state.create.isLoading = false;
				state.create.error = action.payload;
			})

			.addCase(updateRegion.pending, (state) => {
				state.update.isLoading = true;
				state.update.success = false;
				state.update.error = null;
			})
			.addCase(updateRegion.fulfilled, (state) => {
				state.update.isLoading = false;
				state.update.success = true;
			})
			.addCase(updateRegion.rejected, (state, action) => {
				state.update.isLoading = false;
				state.update.error = action.payload;
			})

			.addCase(assignRegionalManager.pending, (state) => {
				state.assign.isLoading = true;
				state.assign.success = false;
				state.assign.error = null;
			})
			.addCase(assignRegionalManager.fulfilled, (state) => {
				state.assign.isLoading = false;
				state.assign.success = true;
			})
			.addCase(assignRegionalManager.rejected, (state, action) => {
				state.assign.isLoading = false;
				state.assign.error = action.payload;
			})

			.addCase(unassignRegionalManager.pending, (state) => {
				state.unassign.isLoading = true;
				state.unassign.success = false;
				state.unassign.error = null;
			})
			.addCase(unassignRegionalManager.fulfilled, (state) => {
				state.unassign.isLoading = false;
				state.unassign.success = true;
			})
			.addCase(unassignRegionalManager.rejected, (state, action) => {
				state.unassign.isLoading = false;
				state.unassign.error = action.payload;
			});
	},
});

export const { resetCreateState, resetUpdateState, resetDetailState, resetAssignState, resetUnassignState } = regionSlice.actions;
export default regionSlice.reducer;
